/* eslint-disable @typescript-eslint/no-explicit-any */
import { EventEmitter } from '@angular/core';
import { AbstractControlOptions, FormControl, ValidatorFn, Validators } from '@angular/forms';
import { merge, Observable, Subscription } from 'rxjs';
import { debounceTime, tap } from 'rxjs/operators';
import { BootstrapSize } from '../bootstrap-size';
import { InternalStatusMessage, ValidationStatus } from '../validations';
import { InputStatus } from '../validations/input-status';
import { AbstractInputConstructor } from './abstract-input';

/**
 * Represents the type of validator or options for form control.
 */
export type ValidatorOrOptions = ValidatorFn | ValidatorFn[] | AbstractControlOptions | null;

/**
 * Represents the constructor options for a base input field.
 *
 * @template T - The type of the input value, default is string.
 */
export type BaseInputConstructor<T = string> = AbstractInputConstructor<T> & {
    /**
     * The initial value of the input.
     */
    value?: T;

    placeholderWithParams?: {
        text: string;
        params: any;
    };
    
    /**
     * Testo per il tooltip dell'icona informativa
     */
    infoButtonText?: string;

    align?: string;
    toHide?: boolean;
    generic?: boolean;
    hideID?: boolean;
    id?: string;
    notTextValidation?: boolean;

    /**
     * Testo informativo sotto il form control
     *
     * @type {?string}
     */
    helpText?: string;
};

export const TOUCHED = 'TOUCHED';
export const UNTOUCHED = 'UNTOUCHED';
export const DIRTY = 'DIRTY';
export const PRISTINE = 'PRISTINE';

export abstract class BaseInput<T = string> extends FormControl {
    /**
     * Specifies the type of input.
     */
    readonly inputType!: string;

    /**
     * The current value of the input.
     */
    override readonly value: T | undefined;

    /**
     * The size of the input.
     */
    readonly size?: BootstrapSize;

    /**
     * The label of the input.
     */
    readonly label: string = '';

    /**
     * The class of the input.
     */
    readonly inputClass: string[] = [];

    /**
     * The class of the label.
     */
    readonly labelClass: string[] = [];

    /**
     * The placeholder text for the input.
     */
    readonly placeholder: string = '';

    /**
     * The description of the input.
     */
    readonly description: string = '';

    /**
     * The index of the input.
     */
    readonly index?: number;

    /**
     * Indicates if the input is clearable.
     */
    readonly clearable: boolean = false;

    /**
     * Indicates if the input is required.
     */
    readonly required: boolean = false;

    /**
     * Indicates if the input is read-only.
     */
    readonly readonly: boolean = false;

    /**
     * The pattern for the input validation.
     */
    readonly pattern?: string;

    /**
     * The input status of the control. There are four possible
     * status values:
     *
     * * **DIRTY**: This control has been modified.
     * * **PRISTINE**: This control has not been modified yet.
     * * **TOUCHED**: This control has been touched.
     * * **UNTOUCHED**: This control has not been touched yet..
     *
     * These status values are mutually exclusive, so a control cannot be
     * both dirty AND pristine or touched AND untouched.
     */
    readonly inputStatus: string | undefined;

    /**
     * An observable for input status changes.
     */
    readonly inputStatusChanges!: Observable<any>;

    /**
     * Callback for value changes of the input.
     */
    readonly valueChange?: (value: T, index?: number) => void;

    /**
     * Array of validation status.
     */
    readonly validationStatus?: ValidationStatus[];

    /**
     * CSS classes for the form group.
     */
    readonly formGroupClass?: string[];

    /**
     * The status message of the input.
     */
    readonly statusMessage: InternalStatusMessage | null;

    readonly placeholderWithParams?: {
        text: string;
        params: any;
    };
    
    /**
     * Testo per il tooltip dell'icona informativa
     */
    readonly infoButtonText?: string;

    readonly toHide?: boolean;
    readonly generic?: boolean;
    readonly hideID?: boolean;
    readonly id?: string;
    readonly notTextValidation?: boolean;

    readonly __internalConstructor__: BaseInputConstructor<T>;
    readonly align?: string;

    readonly helpText?: string;

    private _subscriptions: Subscription[] = [];
    private _ignoreValueChanges = false;

    /**
     * Creates a new instance of BaseInput.
     *
     * @param options - The options for configuring the base input.
     *
     * @property {T} [options.value] - The initial value of the input.
     * @property {string} [options.label] - The label of the input.
     * @property {string | BootstrapSize} [options.size] - The size of the input.
     * @property {string} [options.placeholder] - The placeholder text for the input.
     * @property {string} [options.description] - The description of the input.
     * @property {number} [options.index] - The index of the input.
     * @property {boolean} [options.clearable] - Indicates if the input is clearable.
     * @property {boolean} [options.required] - Indicates if the input is required.
     * @property {boolean} [options.readonly] - Indicates if the input is read-only.
     * @property {string} [options.pattern] - The pattern for the input validation.
     * @property {(value: T) => void} [options.valueChange] - Function to handle value changes.
     * @property {ValidationStatus[]} [options.validationStatus] - Array of validation status.
     * @property {ValidatorOrOptions} [options.validatorOrOpts] - Validator or options for form control.
     * @property {AsyncValidatorFn | AsyncValidatorFn[] | null} [options.asyncValidator] - Async validator for form control.
     * @property {string[]} [options.formGroupClass] - CSS classes for the form group.
     */
    constructor(options: BaseInputConstructor<T>) {
        super(options.value, options.validatorOrOpts, options.asyncValidator);

        this.__internalConstructor__ = options;
        const validators: ValidatorFn[] = [];

        this.formGroupClass = options.formGroupClass || [];

        this._initSize(options.size);
        this._initInputStatusObservable();
        this._initStatusChanges();

        if (options.label) {
            this.label = options.label;
        }

        if (options.inputClass) {
            this.inputClass = options.inputClass;
        }

        if (options.labelClass) {
            this.labelClass = options.labelClass;
        }

        if (this.validator) {
            validators.push(this.validator);
        }

        if (options.align) {
            this.align = options.align;
        }

        if (options.placeholder) {
            this.placeholder = options.placeholder;
        }

        if (options.placeholderWithParams) {
            this.placeholderWithParams = options.placeholderWithParams;
        }
        
        if (options.infoButtonText) {
            this.infoButtonText = options.infoButtonText;
        }

        if (options.index) {
            this.index = options.index;
        }

        if (options.clearable) {
            this.clearable = options.clearable;
        }

        if (options.required) {
            this.required = options.required;
            validators.push(Validators.required);
        }

        if (options.readonly) {
            this.readonly = options.readonly;
            this.disable();
        }

        if (options.pattern) {
            this.pattern = options.pattern;
            validators.push(Validators.pattern(options.pattern));
        }

        if (options.valueChange) {
            this.valueChange = options.valueChange;
            this._initValueChanges();
        }

        if (options.validationStatus) {
            this.validationStatus = options.validationStatus;
        }

        this.statusMessage = this._getStatusMessage();

        if (options.toHide) {
            this.toHide = options.toHide;
        }
        this.generic = false;
        if (options.generic) {
            this.generic = options.generic;
        }

        this.hideID = false;
        if (options.hideID) {
            this.hideID = options.hideID;
        }

        if (options?.id) {
            this.id = options.id;
        }

        if ('undefined' !== typeof options.helpText) {
            this.helpText = options.helpText;
        }

        this.notTextValidation = options.notTextValidation ?? true;

        this.setValidators(validators);
    }

    set ignoreValueChanges(value: boolean) {
        this._ignoreValueChanges = value;
    }

    /**
     * Checks if the input is required.
     *
     * @returns - Returns true if the input is required, otherwise false.
     */
    isRequired(): boolean {
        let required = false;

        if (this.required === true) {
            required = true;
        }

        if (this.validator) {
            const validationResult = this.validator(new FormControl());
            required = validationResult !== null && validationResult['required'] === true;
        }

        return required;
    }

    /**
     * Unsubscribes from all subscriptions.
     */
    unsubscribeSubscriptions(): void {
        this._subscriptions.forEach((subscription) => {
            subscription.unsubscribe();
        });
    }

    /**
     * Sets the size of the input.
     *
     * @param size - The size of the input.
     */
    setSize(size: string | BootstrapSize): void {
        this._initSize(size);
    }

    /**
     * Sets the index of the input.
     *
     * @param index - The index of the input.
     */
    setIndex(index: number): void {
        (this as { index: number }).index = index;
    }

    setValidationStatus(status: ValidationStatus) {
        this.validationStatus?.push(status);
    }

    /**
     * Sets the value of the input.
     *
     * @param value - The value of the input.
     * @param [options] - Options for setting the value.
     */
    override setValue(
        value: any,
        options?: {
            onlySelf?: boolean;
            emitEvent?: boolean;
            emitModelToViewChange?: boolean;
            emitViewToModelChange?: boolean;
        }
    ): void {
        super.setValue(value, options);
        (this as { value: any }).value = value;
    }

    /**
     * Patches the value of the input.
     *
     * @param value - The value of the input.
     * @param [options] - Options for patching the value.
     */
    override patchValue(
        value: any,
        options?: {
            onlySelf?: boolean;
            emitEvent?: boolean;
            emitModelToViewChange?: boolean;
            emitViewToModelChange?: boolean;
        }
    ): void {
        super.patchValue(value, options);
    }

    /**
     * Marks the input as touched.
     *
     * @param [opts] - Options for marking as touched.
     */
    override markAsTouched(opts: { onlySelf?: boolean; emitEvent?: boolean } = {}): void {
        super.markAsTouched(opts);

        (this as { inputStatus: string }).inputStatus = TOUCHED;

        if (opts.emitEvent !== false) {
            (this.inputStatusChanges as EventEmitter<any>).emit(this.inputStatus);
        }
    }

    /**
     * Marks the input as untouched.
     *
     * @param [opts] - Options for marking as untouched.
     */
    override markAsUntouched(opts: { onlySelf?: boolean; emitEvent?: boolean } = {}): void {
        super.markAsUntouched(opts);

        (this as { inputStatus: string }).inputStatus = UNTOUCHED;

        if (opts.emitEvent !== false) {
            (this.inputStatusChanges as EventEmitter<any>).emit(this.inputStatus);
        }
    }

    /**
     * Marks the input as dirty.
     *
     * @param [opts] - Options for marking as dirty.
     */
    override markAsDirty(opts: { onlySelf?: boolean; emitEvent?: boolean } = {}): void {
        super.markAsDirty(opts);

        (this as { inputStatus: string }).inputStatus = DIRTY;

        if (opts.emitEvent !== false) {
            (this.inputStatusChanges as EventEmitter<any>).emit(this.inputStatus);
        }
    }

    /**
     * Marks the input as pristine.
     *
     * @param [opts] - Options for marking as pristine.
     */
    override markAsPristine(opts: { onlySelf?: boolean; emitEvent?: boolean } = {}): void {
        super.markAsPristine(opts);

        (this as { inputStatus: string }).inputStatus = PRISTINE;

        if (opts.emitEvent !== false) {
            (this.inputStatusChanges as EventEmitter<any>).emit(this.inputStatus);
        }
    }

    /**
     * Sets the validators for the input.
     *
     * @param newValidator - The new validators for the input.
     */
    override setValidators(newValidator: ValidatorFn | ValidatorFn[] | null): void {
        super.setValidators(newValidator);
        (this as { required: any }).required = this.isRequired();
    }

    /**
     * Clears the validators for the input.
     */
    override clearValidators(): void {
        super.clearValidators();
        (this as { required: any }).required = false;
        this.setErrors(null);
    }

    /**
     * Enables the input.
     *
     * @param [opts] - Options for enabling the input.
     */
    override enable(opts?: { onlySelf?: boolean; emitEvent?: boolean }): void {
        super.enable(opts);
        (this as { readonly: any }).readonly = false;
    }

    /** @internal */
    private _initValueChanges(): void {
        // Initializes the value changes event.
        this._subscriptions.push(this.valueChanges.subscribe(this._nextValueChanges.bind(this)));
    }

    /** @internal */
    private _nextValueChanges(value: T) {
        if (!this.valueChange || this._ignoreValueChanges) {
            return;
        }
        this.valueChange(value);
    }

    /** @internal */
    private _initStatusChanges(): void {
        this._subscriptions.push(
            merge(this.inputStatusChanges, this.statusChanges, this.valueChanges)
                .pipe(
                    debounceTime(10),
                    tap(() => {
                        (
                            this as {
                                statusMessage: any;
                            }
                        ).statusMessage = this._getStatusMessage();
                    })
                )
                .subscribe()
        );
    }

    /** @internal */
    private _initSize(size: string | BootstrapSize | undefined): void {
        if (typeof size === 'string') {
            (this as { size: any }).size = BootstrapSize.formPipe(size);
        } else if (size instanceof BootstrapSize) {
            (this as { size: any }).size = size;
        } else {
            (this as { size: any }).size = new BootstrapSize();
        }
    }

    /** @internal */
    private _initInputStatusObservable() {
        (
            this as {
                inputStatusChanges: Observable<any>;
            }
        ).inputStatusChanges = new EventEmitter();
    }

    private _getStatusMessage(): InternalStatusMessage | null {
        // console.log('baseInput.validationStatus', this.id, this.validationStatus);
        if (this.validationStatus && Array.isArray(this.validationStatus)) {
            for (const vs of this.validationStatus) {
                if ((vs as InputStatus).checkFn(this)) {
                    // console.log('baseInput.validationStatus', (vs as InputStatus).message);
                    return (vs as InputStatus).message;
                }
            }
        }

        return null;
    }

    abstract clone(): BaseInput<T>;
}
